import { useState, useEffect, useCallback } from 'react';
import { locationsAPI } from '../services/api';

const locationCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000;

const useLocationData = () => {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = useCallback(async (pathSuffix, cacheKey) => {
    const cached = locationCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    try {
      const response = await locationsAPI.get(pathSuffix);
      const data = response.data.data;

      locationCache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });

      return data;
    } catch (err) {
      console.error(`Error fetching ${cacheKey}:`, err);
      throw err;
    }
  }, []);

  const fetchCountries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchData('/countries', 'countries');
      setCountries(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to fetch countries');
      console.error('Error fetching countries:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchData]);

  const fetchStates = useCallback(
    async (countryId) => {
      if (!countryId) {
        setStates([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await fetchData(`/states?country=${countryId}`, `states_${countryId}`);
        setStates(data);
      } catch (err) {
        setError('Failed to fetch states');
        console.error('Error fetching states:', err);
      } finally {
        setLoading(false);
      }
    },
    [fetchData]
  );

  const fetchCities = useCallback(
    async (stateId) => {
      if (!stateId) {
        setCities([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await fetchData(`/cities?state=${stateId}`, `cities_${stateId}`);
        setCities(data);
      } catch (err) {
        setError('Failed to fetch cities');
        console.error('Error fetching cities:', err);
      } finally {
        setLoading(false);
      }
    },
    [fetchData]
  );

  const refreshAllData = useCallback(async () => {
    locationCache.clear();
    await fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      let needsRefresh = false;

      for (const [, value] of locationCache.entries()) {
        if (now - value.timestamp > CACHE_DURATION) {
          needsRefresh = true;
          break;
        }
      }

      if (needsRefresh) {
        console.log('🔄 Auto-refreshing location data...');
        refreshAllData();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [refreshAllData]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  return {
    countries,
    states,
    cities,
    loading,
    error,
    lastUpdated,
    fetchCountries,
    fetchStates,
    fetchCities,
    refreshAllData,
    clearCache: () => locationCache.clear()
  };
};

export default useLocationData;
